import type { PermissionMapping } from './permissions-types';

// API route permissions: admin APIs
export const ADMIN_API_PERMISSIONS: PermissionMapping[] = [
  // Admin APIs
  {
    path: '/api/admin/users',
    permission: 'admin.view',
    name: 'View Users API',
    description: 'Access to user analytics and search API',
  },
  {
    path: '/api/admin/users/retention',
    permission: 'admin.view',
    name: 'User Retention API',
    description: 'Weekly signup-cohort retention grid',
  },
  {
    path: '/api/admin/users/engagement',
    permission: 'admin.view',
    name: 'User Engagement API',
    description: 'Daily bounce rate and session length of logged-in users',
  },
  {
    path: '/api/admin/users/[sub]',
    permission: 'admin.view',
    name: 'Manage User API',
    description: 'Hard-delete a user and override their tier',
  },
  {
    path: '/api/admin/crm',
    permission: 'admin.view',
    name: 'CRM API',
    description: 'M&A sales pipeline API',
  },
  {
    path: '/api/admin/crm/[id]',
    permission: 'admin.view',
    name: 'CRM Lead API',
    description: 'M&A sales pipeline single-lead API',
  },
  {
    path: '/api/admin/referrers',
    permission: 'admin.view',
    name: 'View Referrers API',
    description: 'Referrer source statistics API',
  },
  {
    path: '/api/admin/subscriptions',
    permission: 'admin.subscriptions',
    name: 'Manage Subscriptions API',
    description: 'Access to subscriptions API',
  },
  {
    path: '/api/admin/stripe-events',
    permission: 'admin.stripe-events',
    name: 'View Stripe Events API',
    description: 'Access to Stripe events API',
  },

  {
    path: '/api/account',
    permission: null,
    name: 'View Own Account API',
    description: 'Access to own account API (all authenticated users)',
  },
  {
    path: '/api/account/profile',
    permission: null,
    name: 'Update Own Account Profile API',
    description: 'Access to own account profile API (all authenticated users)',
  },
  {
    path: '/api/account/preferences',
    permission: null,
    name: 'Update Own Preferences API',
    description: 'Save own language preference (all authenticated users)',
  },
  {
    path: '/api/tenders',
    permission: null,
    name: 'View Tenders API',
    description: 'Access to TED tender notices API (all authenticated users)',
  },
  {
    path: '/api/admin/settings/maintenance',
    permission: 'admin.view',
    name: 'Manage Maintenance Mode API',
    description: 'Toggle site-wide maintenance mode',
  },
  {
    path: '/api/admin/website',
    permission: 'admin.view',
    name: 'Manage Company Websites API',
    description: 'Access to company website management API',
  },
  {
    path: '/api/admin/overrides',
    permission: 'admin.view',
    name: 'Manage Company Overrides API',
    description: 'Access to company override management API',
  },
  {
    path: '/api/admin/files',
    permission: 'admin.view',
    name: 'Manage Company Files API',
    description: 'Access to company file upload management API',
  },
  {
    path: '/api/admin/coverage',
    permission: 'admin.view',
    name: 'View Coverage API',
    description: 'Access to data coverage metrics API',
  },
  {
    path: '/api/admin/privacy',
    permission: 'admin.view',
    name: 'Manage Private Companies API',
    description: 'Access to private company management API',
  },
  {
    path: '/api/admin/api',
    permission: 'admin.view',
    name: 'Manage API Keys (Admin)',
    description: 'Create and list API keys across all customers',
  },
  {
    path: '/api/admin/api/[id]',
    permission: 'admin.view',
    name: 'Revoke API Key (Admin)',
    description: 'Revoke any API key',
  },
  {
    path: '/api/admin/api/usage',
    permission: 'admin.view',
    name: 'View API Usage (Admin)',
    description: 'Aggregated and per-customer/per-sub API usage',
  },
  {
    path: '/api/admin/api/logs',
    permission: 'admin.view',
    name: 'View API Request Log (Admin)',
    description: 'Per-call request log filtered by sub, customer, or key',
  },
  {
    path: '/api/admin/logs/search-traffic',
    permission: 'admin.view',
    name: 'Search Traffic (Admin)',
    description: 'Daily engine landings and search console clicks',
  },
  {
    path: '/api/admin/credits',
    permission: 'admin.view',
    name: 'Grant Credits (Admin)',
    description: 'Grant or reset credits for any sub',
  },
  {
    path: '/api/admin/emails/preview',
    permission: 'admin.view',
    name: 'Preview Emails (Admin)',
    description: 'Render a transactional email template to HTML',
  },
  {
    path: '/api/admin/emails/send-test',
    permission: 'admin.view',
    name: 'Send Test Email (Admin)',
    description: 'Send one template through the real mail transport',
  },
  {
    path: '/api/admin/emails/log',
    permission: 'admin.view',
    name: 'View Sent Email Log (Admin)',
    description: 'List the emails actually sent to users',
  },
  {
    path: '/api/admin/emails/log/[id]',
    permission: 'admin.view',
    name: 'View Sent Email Body (Admin)',
    description: 'Read one logged email as the recipient received it',
  },
  {
    path: '/api/admin/publications/overview',
    permission: 'admin.view',
    name: 'Publications Overview (Admin)',
    description: 'Regular and proposed topics, running job, spend today',
  },
  {
    path: '/api/admin/publications/templates',
    permission: 'admin.view',
    name: 'Publication Templates (Admin)',
    description: 'List publication templates and parameter schemas',
  },
  {
    path: '/api/admin/publications/topics',
    permission: 'admin.view',
    name: 'Manage Publication Topics (Admin)',
    description: 'List and create publication topics',
  },
  {
    path: '/api/admin/publications/topics/[id]/restore',
    permission: 'admin.view',
    name: 'Restore Publication Topic (Admin)',
    description: 'Restore dismissed publication topics',
  },
  {
    path: '/api/admin/publications/topics/generate',
    permission: 'admin.view',
    name: 'Generate Publication Topics (Admin)',
    description: 'Start a topic-generation job',
  },
  {
    path: '/api/admin/publications/topics/[id]',
    permission: 'admin.view',
    name: 'View Publication Topic (Admin)',
    description: 'Topic with fact pack and probe',
  },
  {
    path: '/api/admin/publications/topics/[id]/dismiss',
    permission: 'admin.view',
    name: 'Dismiss Publication Topic (Admin)',
    description: 'Dismiss a proposed topic',
  },
  {
    path: '/api/admin/publications/topics/[id]/pack',
    permission: 'admin.view',
    name: 'Rebuild Publication Fact Pack (Admin)',
    description: 'Force a fact-pack rebuild for a topic',
  },
  {
    path: '/api/admin/publications/topics/[id]/posts',
    permission: 'admin.view',
    name: 'Create Publication Post (Admin)',
    description: 'Start a post-writing job for a topic',
  },
  {
    path: '/api/admin/publications/posts/[id]',
    permission: 'admin.view',
    name: 'View Publication Post (Admin)',
    description: 'Post with all revisions',
  },
  {
    path: '/api/admin/publications/posts/[id]/regenerate',
    permission: 'admin.view',
    name: 'Regenerate Publication Post (Admin)',
    description: 'Write a new revision of a post',
  },
  {
    path: '/api/admin/publications/posts/[id]/mark-published',
    permission: 'admin.view',
    name: 'Mark Publication Published (Admin)',
    description: 'Record that a post went out on LinkedIn',
  },
  {
    path: '/api/admin/publications/jobs/[id]',
    permission: 'admin.view',
    name: 'View Publication Job (Admin)',
    description: 'Poll a topic or post job',
  },
  {
    path: '/api/admin/publications/settings',
    permission: 'admin.view',
    name: 'Publication Settings (Admin)',
    description: 'Voice samples, banlist and daily spend cap',
  },
  {
    path: '/api/admin/publications/history',
    permission: 'admin.view',
    name: 'Publication History (Admin)',
    description: 'Published posts',
  },
  {
    path: '/api/admin/credits/grant-all',
    permission: 'admin.view',
    name: 'Bulk Grant Credits (Admin)',
    description: 'Grant credits to every user profile in one call',
  },
  {
    path: '/api/admin/credits/[sub]',
    permission: 'admin.view',
    name: 'Inspect Credits (Admin)',
    description: "View a sub's credit balance and ledger",
  },
  {
    path: '/api/admin/funnel',
    permission: 'admin.view',
    name: 'Funnel Data (Admin)',
    description: 'Weekly conversion counts across the funnel',
  },
  {
    path: '/api/admin/dashboard',
    permission: 'admin.view',
    name: 'Admin Dashboard API',
    description: 'Revenue, visitors, signups and product usage',
  },
  {
    path: '/api/admin/scrape-freshness',
    permission: 'admin.view',
    name: 'Scrape Freshness API',
    description: 'Daily delay from register publication to first record',
  },
  {
    path: '/api/admin/credits/reconcile',
    permission: 'admin.view',
    name: 'Reconcile Top-ups (Admin)',
    description: 'Stripe top-up payments with no matching credit grant',
  },
  {
    path: '/api/admin/credits/users',
    permission: 'admin.view',
    name: 'List Credit Accounts (Admin)',
    description: 'Paginated users with plan, balance and lifetime spend',
  },
  {
    path: '/api/admin/watchlist',
    permission: 'admin.view',
    name: 'List Watchers (Admin)',
    description: 'Users holding at least one watched company',
  },
  {
    path: '/api/admin/watchlist/[sub]',
    permission: 'admin.view',
    name: 'Inspect Watchlist (Admin)',
    description: 'The companies a given user watches',
  },
];
